import { getHostUrl } from "@/composables/store";
import { useUser } from "@/stores/user";

export type UserEndpointPermission = {
	endpoint_id: number;
	method: string;
	path: string;
	credit_type_id: number | null;
	is_active: boolean;
	credit_type: string | null;
	is_internal?: boolean;
};

type UserPermissionsResponse = {
	success?: boolean;
	data?: UserEndpointPermission[];
	detail?: string;
	message?: string;
};

// Wallet credit type behind GET /v1/ucc/companies and /v1/ucc/companies/platform.
// Turning it off for a user makes both return 403, so the company overview and
// the monitoring workspace built on top of it have to check it before linking.
export const UCC_COMPANY_OVERVIEW_CREDIT_TYPE = "UCC_COMPANIES";
export const UCC_RECOMMENDATIONS_CREDIT_TYPE = "UCC_RECOMMENDATIONS";

const PERMISSIONS_STATE_KEY = "user-endpoint-permissions";

// Keyed by the Nuxt app so a server render de-duplicates within one request
// without sharing an in-flight promise between users.
const inFlightByApp = new WeakMap<object, Promise<UserEndpointPermission[]>>();

function resolveToken(): string {
	const value = useUser().token as unknown;
	if (typeof value === "string") return value;
	if (value && typeof value === "object" && "value" in value) {
		const nested = (value as { value?: unknown }).value;
		return typeof nested === "string" ? nested : "";
	}
	return "";
}

async function requestPermissions(): Promise<UserEndpointPermission[]> {
	const token = resolveToken();
	if (!token) return [];

	const response = await fetch(`${getHostUrl()}/users/permissions`, {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${token}`,
		},
	});

	const payload = (await response.json().catch(() => ({}))) as UserPermissionsResponse;
	if (!response.ok || payload?.success !== true) {
		throw new Error(payload?.detail || payload?.message || `Failed to load permissions (${response.status})`);
	}

	return Array.isArray(payload.data) ? payload.data : [];
}

export function useEndpointPermissions() {
	// /v1/users/permissions only returns endpoints whose wallet row is active, so
	// a disabled endpoint is absent from the list rather than present with
	// is_active false. Absence means "no access", which is also the safe default
	// while the list is still loading.
	const permissions = useState<UserEndpointPermission[] | null>(PERMISSIONS_STATE_KEY, () => null);
	const nuxtApp = useNuxtApp();

	// Resolved once per request on the server and carried into the client through
	// the payload. A failed load stays uncached so the next caller retries rather
	// than leaving every gated control hidden for good.
	async function loadEndpointPermissions(force = false): Promise<UserEndpointPermission[]> {
		if (!force && permissions.value) return permissions.value;

		const pending = inFlightByApp.get(nuxtApp);
		if (pending && !force) return pending;

		const request = requestPermissions()
			.then((rows) => {
				permissions.value = rows;
				return rows;
			})
			.catch((error) => {
				console.error("Failed to load endpoint permissions:", error);
				return [] as UserEndpointPermission[];
			})
			.finally(() => {
				if (inFlightByApp.get(nuxtApp) === request) inFlightByApp.delete(nuxtApp);
			});

		inFlightByApp.set(nuxtApp, request);
		return request;
	}

	function hasCreditTypeAccess(creditType: string): boolean {
		return (permissions.value || []).some((row) => row.credit_type === creditType && row.is_active !== false);
	}

	function hasEndpointAccess(method: string, path: string): boolean {
		const normalizedMethod = method.toUpperCase();
		const normalizedPath = path.replace(/\/+$/, "");
		return (permissions.value || []).some(
			(row) => String(row.method).toUpperCase() === normalizedMethod && String(row.path).replace(/\/+$/, "") === normalizedPath && row.is_active !== false
		);
	}

	function clearEndpointPermissions() {
		permissions.value = null;
	}

	return {
		permissions,
		loadEndpointPermissions,
		clearEndpointPermissions,
		hasCreditTypeAccess,
		hasEndpointAccess,
	};
}

// Convenience for the guards that all key off the same wallet row.
export async function hasUccCompanyOverviewAccess(): Promise<boolean> {
	const { loadEndpointPermissions } = useEndpointPermissions();
	const rows = await loadEndpointPermissions();
	return rows.some((row) => row.credit_type === UCC_COMPANY_OVERVIEW_CREDIT_TYPE && row.is_active !== false);
}

// The global auth middleware loads this state on direct requests and hydrates it
// into the client. Reuse that value for client navigation; every recommendations
// API route remains the authoritative authorization boundary.
export async function hasUccRecommendationsAccess(): Promise<boolean> {
	const { loadEndpointPermissions } = useEndpointPermissions();
	const rows = await loadEndpointPermissions();
	return rows.some((row) => row.credit_type === UCC_RECOMMENDATIONS_CREDIT_TYPE && row.is_active !== false);
}
